import React, { useState } from "react";

const EMICalculator = () => {
    const [loanAmount, setLoanAmount] = useState("5,00,000");
    const [interestRate, setInterestRate] = useState(7.5);
    const [tenure, setTenure] = useState(20); // in years

    const [emi, setEmi] = useState(null);
    const [totalInterest, setTotalInterest] = useState(null);
    const [totalPayment, setTotalPayment] = useState(null);

    const [errors, setErrors] = useState({
        loanAmount: "",
        interestRate: "",
        tenure: "",
    });

    const parseNumber = (formattedValue) => {
        return Number(formattedValue.replace(/,/g, ""));
    };

    const formatCurrency = (amount) =>
        amount
            ? "₹" + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "-";

    const handleAmountChange = (e) => {
        const raw = e.target.value.replace(/,/g, "");
        if (!isNaN(raw)) {
            const formatted = Number(raw).toLocaleString("en-IN");
            setLoanAmount(formatted);
            setErrors(prev => ({ ...prev, loanAmount: "" }));
        }
    };

    const handleTenureChange = (e) => {
        const raw = e.target.value.replace(/^0+/, "");
        if (!isNaN(raw) && raw.length <= 2) {
            setTenure(raw === "" ? "" : Number(raw));
            setErrors(prev => ({ ...prev, tenure: "" }));
        }
    };

    const handleInterestChange = (e) => {
        const value = e.target.value;
        setInterestRate(value);
        setErrors(prev => ({ ...prev, interestRate: "" }));
    };

    const validate = () => {
        const newErrors = {};

        if (!loanAmount || parseNumber(loanAmount) <= 0) {
            newErrors.loanAmount = "Loan amount is required and must be greater than 0.";
        }

        if (!interestRate || interestRate <= 0) {
            newErrors.interestRate = "Interest rate is required and must be greater than 0.";
        }

        if (!tenure || tenure <= 0) {
            newErrors.tenure = "Tenure is required and must be greater than 0.";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const calculateEMI = () => {
        if (!validate()) return;

        const P = parseNumber(loanAmount);
        const r = interestRate / 12 / 100;
        const N = tenure * 12;

        const emiCalc = (P * r * Math.pow(1 + r, N)) / (Math.pow(1 + r, N) - 1);
        const total = emiCalc * N;
        const interest = total - P;

        setEmi(emiCalc);
        setTotalPayment(total);
        setTotalInterest(interest);
    };

    return (
        <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center py-[140px] px-4">
            <div className="bg-white shadow-xl rounded-lg p-8 w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Section - Form */}
                <div>
                    <h2 className="text-2xl font-bold mb-6 text-blue-600">EMI Calculator</h2>

                    <div className="space-y-5">
                        {/* Loan Amount */}
                        <div>
                            <label className="block font-medium mb-1">Loan Amount (₹)</label>
                            <input
                                type="text"
                                value={loanAmount}
                                onChange={handleAmountChange}
                                className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 ${errors.loanAmount ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'}`}
                            />
                            {errors.loanAmount && <p className="text-sm text-red-500 mt-1">{errors.loanAmount}</p>}
                        </div>

                        {/* Interest Rate */}
                        <div>
                            <label className="block font-medium mb-1">Interest Rate (% per annum)</label>
                            <input
                                type="number"
                                value={interestRate}
                                onChange={handleInterestChange}
                                className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 ${errors.interestRate ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'}`}
                                step="0.1"
                                min={0.1}
                            />
                            {errors.interestRate && <p className="text-sm text-red-500 mt-1">{errors.interestRate}</p>}
                        </div>

                        {/* Tenure */}
                        <div>
                            <label className="block font-medium mb-1">Loan Tenure (years)</label>
                            <input
                                type="text"
                                value={tenure}
                                onChange={handleTenureChange}
                                className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 ${errors.tenure ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'}`}
                            />
                            {errors.tenure && <p className="text-sm text-red-500 mt-1">{errors.tenure}</p>}
                        </div>

                        <button
                            onClick={calculateEMI}
                            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition"
                        >
                            Calculate EMI
                        </button>
                    </div>
                </div>

                {/* Right Section - Results */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-6">
                    <h3 className="text-xl font-semibold mb-4 text-blue-700">Result Summary</h3>
                    {emi ? (
                        <div className="space-y-4 text-lg text-gray-700">
                            <p><strong>Monthly EMI:</strong> <span className="text-blue-700 font-semibold">{formatCurrency(emi)}</span></p>
                            <p><strong>Total Interest:</strong> <span className="text-blue-700 font-semibold">{formatCurrency(totalInterest)}</span></p>
                            <p><strong>Total Payment:</strong> <span className="text-blue-700 font-semibold">{formatCurrency(totalPayment)}</span></p>
                        </div>
                    ) : (
                        <p className="text-gray-500">Enter details and click "Calculate EMI" to see results.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EMICalculator;
